import axiosInstance from "./axiosInstance";
import { AxiosResponse } from "axios";
import fetchIpAddress from "../utils/fetchIP";
import { PaymentProduct5002SpecificInput } from "types/creditCard.types";

// GET request to fetch PBL data using UUID
export const getPBLData = async (uuid: string): Promise<AxiosResponse> => {
    try {
        const response = await axiosInstance.get(`/v1/pay-link/${uuid}`);
        return response;
    } catch (error: any) {
        return error.response;
    }
};

// POST requset for the Invoice method
export const submitInvoicePaymentRequest = async (data: any, ipAddress: string, snippetToken: string) => {
    const { linkId } = data;

    const submittedValue = createPostData(data);
    const modifiedData = {
        requiredParameters: {
            "3390": submittedValue,
        },
        paymentMethodSpecificInput: {
            financingPaymentMethodSpecificInput: {
                paymentProductId: "3390",
            },
            customerDevice: {
                ipAddress,
                deviceToken: snippetToken,
            },
        },
    };

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};

function createPostData(data: any) {

    const keyMap: Record<string, string> = {
        firstName: "customer.personalInformation.name.firstName",
        lastName: "customer.personalInformation.name.surname",
        dob: "customer.personalInformation.dateOfBirth",
        phone: "customer.contactDetails.phoneNumber",
        email: "customer.contactDetails.emailAddress",
        selectedBusiness: "customer.businessRelation",
        companyName: "customer.companyInformation",
        address: "customer.billingAddress.street",
        zipcode: "customer.billingAddress.zip",
        city: "customer.billingAddress.city",
        selectedCountry: "customer.billingAddress.countryCode",
    };

    const countryCodeMap: Record<string, string> = {
        Austria: "AT",
        Belgium: "BE",
        France: "FR",
        Germany: "DE"
    };

    const submittedValue: Record<string, any> = Object.keys(keyMap).reduce(
        (acc: Record<string, any>, key: string) => {
            const value = data[key];

            if (value) {
                if (key === 'phone') {
                    acc[keyMap[key]] = value.replace(/\s+/g, '');
                } else {
                    acc[keyMap[key]] = countryCodeMap[value] || value;
                }
            }

            return acc;
        },
        {}
    );
    return submittedValue;
}

// // POST requset for the Debit
export const submitDirectDebitPaymentRequest = async (data: any, ipAddress: string, snippetToken: string) => {
    const { linkId } = data;

    const submittedValue = createPostDataForDirectDebit(data);
    const modifiedData = {
        requiredParameters: {
            "3392": submittedValue,
        },
        paymentMethodSpecificInput: {
            financingPaymentMethodSpecificInput: {
                paymentProductId: '3392',
                paymentProduct3392SpecificInput: {
                    bankAccountInformation: {
                        iban: data.payment.iban,
                        accountHolder: data.payment.bankAccountHolder
                    }
                }
            },
            customerDevice: {
                ipAddress,
                deviceToken: snippetToken,
            },
        },
    };
    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};

function createPostDataForDirectDebit(data: any) {

    const keyMap: Record<string, string> = {
        firstName: "customer.personalInformation.name.firstName",
        lastName: "customer.personalInformation.name.surname",
        dob: "customer.personalInformation.dateOfBirth",
        phone: "customer.contactDetails.phoneNumber",
        email: "customer.contactDetails.emailAddress",
        businessRelation: "customer.businessRelation",
        companyName: "customer.companyInformation",
        address: "customer.billingAddress.street",
        zipcode: "customer.billingAddress.zip",
        city: "customer.billingAddress.city",
        country: "customer.billingAddress.countryCode",
    };

    const countryCodeMap: Record<string, string> = {
        Germany: "DE",
        Austria: "AT",
    };

    const submittedValue: Record<string, any> = Object.keys(keyMap).reduce(
        (acc: Record<string, any>, key: string) => {
            let value;
            if (key in keyMap) {

                switch (key) {
                    case 'firstName':
                        value = data.customer.firstName;
                        break;
                    case 'lastName':
                        value = data.customer.lastName;
                        break;
                    case 'dob':
                        value = data.customer.dob;
                        break;
                    case 'phone':
                        value = data.contact.phone;
                        break;
                    case 'email':
                        value = data.contact.email;
                        break;
                    case 'businessRelation':
                        value = data.customer.businessRelation;
                        break;
                    case 'companyName':
                        value = data.customer.companyName;
                        break;
                    case 'address':
                        value = data.billing.address;
                        break;
                    case 'zipcode':
                        value = data.billing.zipcode;
                        break;
                    case 'city':
                        value = data.billing.city;
                        break;
                    case 'country':
                        value = data.billing.country;
                        break;
                    default:
                        value = null; // default to null if no mapping
                }
            }

            if (value) {
                if (key === 'phone') {
                    acc[keyMap[key]] = value.replace(/\s+/g, '');
                } else if (key === 'country') {
                    acc[keyMap[key]] = countryCodeMap[value] || value;
                } else {
                    acc[keyMap[key]] = value;
                }
            }

            return acc;
        },
        {}
    );
    return submittedValue;
}

// API request to get installment options
export const getInstallmentOptions = async (data: any, snippetToken: string) => {
    const { linkId } = data;
    const ipAddress = await fetchIpAddress();

    const submittedValue = createPostData(data);
    const modifiedData = {
        requiredParameters: {
            "3391": submittedValue,
        },
        paymentMethodSpecificInput: {
            financingPaymentMethodSpecificInput: {
                paymentProductId: "3391",
            },
            customerDevice: {
                ipAddress,
                deviceToken: snippetToken,
            },
        },
    };

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch installment payment options");
    }
};


// API request to get installment options
export const competeInstallmentOptionPayment = async (data: any) => {
    const { linkId } = data;

    const modifiedData = {
        completePaymentMethodSpecificInput: {
            paymentProduct3391SpecificInput: {
                installmentOptionId: data.installmentOptionId,
                bankAccountInformation: {
                    iban: data.iban,
                    bic: "COLSDE33XXX",
                    accountHolder: data.bankaccholder
                }
            }
        }
    }

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-complete`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to complete installment payment");
    }
};

// POST requset for the Paypal method
export const submitPaypalPaymentRequest = async (data: any, uuid: string) => {
    const { linkId } = data;

    const modifiedData = {
        paymentMethodSpecificInput: {
            redirectPaymentMethodSpecificInput: {
                paymentProductId: "840",
                "redirectionData": {
                    "returnUrl": `https://paylink.dev.commerce.payone.com/${uuid}/status`
                },
            },
        },
    };

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};

// // POST requset for the Crredit Card method
export const submitCreditCardPaymentRequest = async (
    linkId: any,
    cardToken: string,
    paymentMethodId: number,
    snippetToken: string
) => {
    const ipAddress = await fetchIpAddress();
    const modifiedData = {
        paymentMethodSpecificInput: {
            cardPaymentMethodSpecificInput: {
                paymentProductId: paymentMethodId,
                paymentProcessingToken: cardToken
            },
            customerDevice: {
                ipAddress: ipAddress,
                deviceToken: snippetToken
            },
        },
    };

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};

export const submitClickToPayPaymentRequest = async (
    linkId: any,
    paymentMethodId: number,
    paymentProduct5002SpecificInput: PaymentProduct5002SpecificInput,
) => {
    const modifiedData = {
        paymentMethodSpecificInput: {
            mobilePaymentMethodSpecificInput: {
                paymentProductId: paymentMethodId,
                paymentProduct5002SpecificInput,
            }
        },
    };

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};

// POST request for the WERO payment method
export const submitWeroPaymentRequest = async (data: any, snippetToken: string, isCountryCodeRequired: boolean) => {

    const { linkId } = data;
    const ipAddress = await fetchIpAddress();

    let modifiedData: any = {
        paymentMethodSpecificInput: {
            redirectPaymentMethodSpecificInput: {
                paymentProductId: "900",
                "redirectionData": {
                    "returnUrl": "https://secure.ogone.com/ncol/test/displayparams.asp"
                },
            },
        },
        customerDevice: {
            ipAddress,
            deviceToken: snippetToken,
        }
    };

    if (isCountryCodeRequired) {
        const submittedValue = createPostData(data);
        const requiredParameters = {
            "900": submittedValue,
        };
        modifiedData = {
            ...modifiedData,
            requiredParameters
        };
    }

    try {
        const response = await axiosInstance.post(
            `/v1/pay-link/${linkId}/payment-request`,
            modifiedData
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to create item");
    }
};